/**
 * useActivityTracker — logs page visits + login events to the backend.
 * Batches activities and sends every 30 seconds to minimize API calls.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import apiClient from '../services/apiClient';

interface PendingActivity {
  type: string;
  pageUrl?: string;
  entityType?: string;
  entityKey?: string;
  metadata?: Record<string, unknown>;
  durationSeconds?: number;
}

const FLUSH_INTERVAL = 30000; // 30 seconds

export function useActivityTracker() {
  const location = useLocation();
  const queue = useRef<PendingActivity[]>([]);
  const lastPage = useRef<string>('');
  const pageEnterTime = useRef<number>(Date.now());

  const flush = useCallback(async () => {
    if (queue.current.length === 0) return;
    const batch = [...queue.current];
    queue.current = [];
    try {
      await apiClient.post('/analytics/activity', { activities: batch });
    } catch {
      // Non-critical — silently discard on failure
    }
  }, []);

  // Track page visits on route changes
  useEffect(() => {
    const now = Date.now();
    const currentPath = location.pathname;

    // Log duration for previous page
    if (lastPage.current && lastPage.current !== currentPath) {
      const duration = Math.round((now - pageEnterTime.current) / 1000);
      if (duration > 0 && duration < 7200) { // cap at 2 hours
        queue.current.push({
          type: 'page_visit',
          pageUrl: lastPage.current,
          durationSeconds: duration,
        });
      }
    }

    lastPage.current = currentPath;
    pageEnterTime.current = now;
  }, [location.pathname]);

  // Periodic flush
  useEffect(() => {
    const interval = setInterval(flush, FLUSH_INTERVAL);
    // Flush on page unload
    const handleUnload = () => {
      if (lastPage.current) {
        const duration = Math.round((Date.now() - pageEnterTime.current) / 1000);
        if (duration > 0 && duration < 7200) {
          queue.current.push({ type: 'page_visit', pageUrl: lastPage.current, durationSeconds: duration });
        }
      }
      // Use sendBeacon for reliable delivery on unload
      if (queue.current.length > 0 && navigator.sendBeacon) {
        navigator.sendBeacon(
          apiClient.defaults.baseURL + '/analytics/activity',
          new Blob([JSON.stringify({ activities: queue.current })], { type: 'application/json' }),
        );
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleUnload);
      flush();
    };
  }, [flush]);

  // Log login event (call once from AuthProvider on successful auth)
  const logLogin = useCallback(() => {
    queue.current.push({ type: 'login', pageUrl: location.pathname });
  }, [location.pathname]);

  return { logLogin, flush };
}
